import React, { FunctionComponent } from 'react';
import { visuallyHidden } from '@mui/utils';
import {
  Box,
  TableHead,
  TableSortLabel,
  TableCellProps,
  SxProps,
  Checkbox,
} from '@mui/material';
import { StyledTableCell, StyledTableRow } from './TableStyles';

export type THeadLabelItem = {
  id: string;
  label: string;
  align?: TableCellProps['align'];
  width?: number;
  minWidth?: number | string;
  maxWidth?: number | string;
  sortable?: boolean;
  badgeElement?: React.ReactElement;
};

export interface TableHeadCustomProps {
  order: 'asc' | 'desc';
  orderBy: string;
  rowCount?: number;
  headLabels: THeadLabelItem[];
  numSelected?: number;
  sx?: SxProps;
  onSelectAllRows?: (checked: boolean) => void;
  cellProps?: TableCellProps;
  onSort: (id: string) => void;
}

const defaultCellStyles: SxProps = {
  fontSize: '14px',
  fontWeight: 500,
};

const TableHeadCustom: FunctionComponent<TableHeadCustomProps> = ({
  order,
  orderBy,
  rowCount = 0,
  headLabels,
  numSelected = 0,
  onSelectAllRows,
  sx,
  cellProps = {},
  onSort,
}) => {
  return (
    <TableHead sx={sx}>
      <StyledTableRow>
        {onSelectAllRows && (
          <StyledTableCell padding="checkbox">
            <Checkbox
              indeterminate={numSelected > 0 && numSelected < rowCount}
              checked={rowCount > 0 && numSelected === rowCount}
              onChange={(event) => onSelectAllRows(event.target.checked)}
            />
          </StyledTableCell>
        )}

        {headLabels.map((headCell) => (
          <StyledTableCell
            key={headCell.id}
            align={headCell.align || 'left'}
            sortDirection={orderBy === headCell.id ? order : false}
            {...cellProps}
            sx={{
              width: headCell.width,
              minWidth: headCell.minWidth,
              maxWidth: headCell.maxWidth,
              ...defaultCellStyles,
              ...(cellProps.sx || {}),
            }}
          >
            {headCell.sortable && onSort ? (
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={() => onSort(headCell.id)}
              >
                {orderBy === headCell.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </Box>
                ) : null}
                <>
                  {headCell.label}
                  {headCell.badgeElement ? headCell.badgeElement : null}
                </>
              </TableSortLabel>
            ) : (
              <>
                {headCell.label}
                {headCell.badgeElement ? headCell.badgeElement : null}
              </>
            )}
          </StyledTableCell>
        ))}
      </StyledTableRow>
    </TableHead>
  );
};

export default TableHeadCustom;
